import {useQuery} from '@github-ui/react-query'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'
import type {DiffData} from '../types/diff'

interface UseTaskDiffsOptions {
  /** Include full patch content per file (default: false) */
  includePatch?: boolean
  /** Only fetch when true (default: true) */
  enabled?: boolean
}

export function generateTaskDiffsQueryKey(
  repoNwo: string,
  taskId: string,
  baseRef: string,
  headRef: string,
  includePatch?: boolean,
) {
  return ['task-diff', repoNwo, taskId, baseRef, headRef, ...(includePatch ? ['with-patch'] : [])]
}

/**
 * Hook to fetch task diff data for a task.
 * Pass `{ includePatch: true }` to include full patch content per file.
 * Pass `{ enabled: false }` to defer fetching until needed.
 */
export function useTaskDiffs(
  repoNwo: string,
  taskId: string,
  baseRef: string,
  headRef: string,
  options?: UseTaskDiffsOptions,
) {
  const {includePatch = false, enabled = true} = options ?? {}

  return useQuery<DiffData>({
    queryKey: generateTaskDiffsQueryKey(repoNwo, taskId, baseRef, headRef, includePatch),
    queryFn: async () => {
      let url = `/${repoNwo}/tasks/${taskId}/diff?base=${encodeURIComponent(baseRef)}&head=${encodeURIComponent(headRef)}`
      if (includePatch) {
        url += '&include_patch=true'
      }

      const response = await verifiedFetchJSON(url)

      if (!response.ok) {
        throw new Error('Failed to fetch diff data')
      }

      return (await response.json()) as DiffData
    },
    enabled,
  })
}
